import { Core } from "@faire/design-tokens";
import { IFaireMoney } from "@faire/web-api/indigofair/data/IFaireMoney";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { optimizeImageUrlFillRectangle } from "@faire/web/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web/common/localization";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web/common/transform-images";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { Anchor } from "@faire/web/ui/LinkAnchor";
import { observer } from "mobx-react";
import React, { useMemo } from "react";

// adding internal to avoid other usages, but this is an existing usage, so will ignore
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import {
  TileContainer,
  TileImage,
  TileImageContainer,
} from "@faire/retailer/features/CategoryHomepage/__internal__/Tile/styled";
import { formatAndConvertCentsWithPrefix } from "@faire/retailer/lib/currency";

const MAKER_TILE_ASPECT_RATIO = 1;

const BrandTileBase: React.FC<BrandTile.IProps> = (props) => {
  const { strictLocalize } = useStrictLocalization();
  const { brand } = props;
  const { isXLargeAndAbove } = useViewport();

  const wideMakerTileWidth = isXLargeAndAbove ? 385 : 305;
  const wideMakerTileHeight = Math.round(
    MAKER_TILE_ASPECT_RATIO * wideMakerTileWidth
  );

  const brandMinimumDescription = useMemo(() => {
    const brandMinimum = props.brand?.minimum_order_amount?.amount_cents;
    return brandMinimum
      ? strictLocalize(
          {
            defaultMessage: "{moneyAmount} Minimum",
            description: {
              text: "moneyAmount is the minimum a retailer must order to be able to order from this brand",
            },
          },
          DEPRECATED_backwardsCompatibleValues({
            moneyAmount: formatAndConvertCentsWithPrefix({
              cents: brandMinimum,
              round: true,
            }),
          })
        )
      : strictLocalize({
          defaultMessage: "No minimum",
          description: {
            text: "This brand has no minimum amount required for their orders",
          },
        });
  }, [props.brand?.minimum_order_amount?.amount_cents]);

  const isSSR = useIsSSR();

  const brandImageUrl = useMemo(() => {
    const image = props.brand?.squared_image;
    if (!image) {
      return;
    } else {
      return (
        optimizeImageUrl(image, isSSR, {
          height: wideMakerTileHeight,
          width: wideMakerTileWidth,
          resizeMethod: "fill",
          gravity: "auto",
        }) ??
        optimizeImageUrlFillRectangle(
          image,
          isSSR,
          wideMakerTileWidth,
          wideMakerTileHeight
        ) ??
        image.url
      );
    }
  }, [
    isSSR,
    props.brand?.squared_image,
    wideMakerTileHeight,
    wideMakerTileWidth,
  ]);

  return (
    <TileContainer style={{ textAlign: "left" }}>
      <TileImageContainer>
        <Anchor href={brand?.link} rel="noreferrer" target="_blank">
          <TileImage src={brandImageUrl} />
        </Anchor>
      </TileImageContainer>

      <Spacer size={14} />
      <Typography color={Core.text.enabled}>{brand?.name}</Typography>

      <Spacer size={8} />
      <Typography color={Core.text.subdued}>
        {brandMinimumDescription}
      </Typography>
    </TileContainer>
  );
};

export const BrandTile = observer(BrandTileBase);

export namespace BrandTile {
  export interface IProps {
    brand: IBrand | undefined;
  }

  export interface IBrand {
    name: string | undefined;
    minimum_order_amount: IFaireMoney | undefined;
    link: string | undefined;
    squared_image: IImage | undefined;
  }
}
